import PropTypes from 'prop-types';
import {useEffect, useReducer} from 'react';
import {useDispatch} from 'react-redux';
import {Container, Loader, Tab, Divider, Message, MessageHeader} from 'semantic-ui-react';

import {WordPressSiteSSOStatus} from 'helpers/enums';
import httpClient from 'httpClient';
import {addError, addSuccessMessage} from 'state/actions';

import Matomo from '../../Common/Matomo';
import {updateMatomoRegistration} from '../../Common/sharedElements';

import Aliases from './Aliases/Aliases';
import Info from './Info/Info';

import styles from './Environments.module.scss';

function environmentsReducer(state, action) {
  switch (action.type) {
    case 'FETCH_DATA':
      return {
        ...state,
        isLoading: true,
      };
    case 'SET_DATA':
      return {
        ...state,
        wordpress_sites: action.data.wordpress_sites,
        isLoading: false,
      };
    case 'SET_ACTIVE_ENV_TAB':
      return {
        ...state,
        activeTabIndex: action.data.activeTabIndex,
      };
    default:
      throw new Error();
  }
}

const Environments = ({projectName, onWordpressSites, project}) => {
  const initialState = {
    isLoading: false,
    // a dictionary of wordpress-sites(environments) resources in the current wordpress project(site)
    wordpress_sites: {},
    // the current active horizontal tab index
    activeTabIndex: 0,
  };

  const [{isLoading: isFetchingEnvs, wordpress_sites, activeTabIndex}, dispatch] = useReducer(
    environmentsReducer,
    initialState
  );

  const dispatchMessage = useDispatch();

  const getWordpressSites = async projectName => {
    dispatch({
      type: 'FETCH_DATA',
    });

    try {
      const {
        data: {wordpress_sites},
      } = await httpClient.get(`/api/wordpress/${projectName}/wordpress-sites`);

      if (onWordpressSites) {
        await onWordpressSites(wordpress_sites);
      }

      return wordpress_sites;
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!project) {
      return;
    }
    (async () => {
      const wordpress_sites = await getWordpressSites(project.name);

      dispatch({
        type: 'SET_DATA',
        data: {wordpress_sites: wordpress_sites ? wordpress_sites : {}},
      });
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  return (
    <Container className={styles.Environments} textAlign="center">
      <Divider />
      {isFetchingEnvs && !project ? (
        <Loader inline="centered" active />
      ) : (
        Object.entries(wordpress_sites).map(([name, wordpressSite]) => (
          <Tab
            key={name}
            activeIndex={activeTabIndex}
            onTabChange={(_evt, {activeTabIndex}) => {
              dispatch({
                type: 'SET_ACTIVE_ENV_TAB',
                data: {activeTabIndex: activeTabIndex},
              });
            }}
            className={styles.TabContents}
            menu={{secondary: true, pointing: true}}
            panes={[
              {
                menuItem: 'Info',
                render: function info() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Info
                        key={name}
                        projectCategory={project?.category ?? ''}
                        projectName={projectName}
                        url={wordpressSite.hostnames?.[0] ?? ''}
                        status={wordpressSite.status}
                        version={wordpressSite.version}
                        visibility={wordpressSite.visibility}
                        ssoStatus={wordpressSite.ssoStatus}
                        onSave={async values => {
                          const payload = {
                            ...(values.url && {primaryHostname: values.url}),
                            ...(values.visibility && {visibility: values.visibility}),
                            ...(values.hasOwnProperty('ssoEnabled') && {
                              ssoStatus: values.ssoEnabled
                                ? WordPressSiteSSOStatus.ENABLED
                                : WordPressSiteSSOStatus.DISABLED,
                            }),
                          };

                          await httpClient.patch(`/api/wordpress/${projectName}/${name}`, payload);

                          dispatchMessage(
                            addSuccessMessage(`Information for ${name} has been updated.`)
                          );

                          if (values.url) {
                            dispatchMessage(
                              addSuccessMessage(
                                `The new URL will be reachable in a few seconds. Please note that it may take some time for the old URL to be available for reuse again.`
                              )
                            );

                            if (wordpressSite.matomoSiteId) {
                              try {
                                await updateMatomoRegistration(
                                  wordpressSite.matomoSiteId,
                                  values.url,
                                  dispatchMessage
                                );
                                dispatchMessage(
                                  addSuccessMessage(
                                    'The URL of the Matomo registration has been updated'
                                  )
                                );
                              } catch (err) {
                                dispatchMessage(addError(err.message));
                              }
                            }
                          }

                          const wordpress_sites = await getWordpressSites(projectName);
                          dispatch({
                            type: 'SET_DATA',
                            data: {wordpress_sites: wordpress_sites},
                          });
                        }}
                        onDelete={async () => {
                          await httpClient.delete(
                            `/api/wordpress/${projectName}/wordpress-sites/${name}`
                          );

                          const wordpress_sites = await getWordpressSites(projectName);

                          dispatch({
                            type: 'SET_DATA',
                            data: {wordpress_sites: wordpress_sites},
                          });
                        }}
                      />
                    </Tab.Pane>
                  );
                },
              },
              {
                menuItem: 'Aliases',
                render: function aliases() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Aliases
                        key={name}
                        aliases={wordpressSite.hostnames?.slice(1)}
                        onAliasCreate={async values => {
                          const payload = {};

                          if (values) {
                            payload.alias = values;
                          }
                          await httpClient.post(
                            `/api/wordpress/${projectName}/wordpress-sites/${name}/aliases`,
                            payload
                          );

                          dispatchMessage(addSuccessMessage(`Νew alias ${values} has been added`));

                          const wordpress_sites = await getWordpressSites(projectName);
                          dispatch({
                            type: 'SET_DATA',
                            data: {wordpress_sites: wordpress_sites},
                          });

                          dispatch({
                            type: 'SET_ACTIVE_ENV_TAB',
                            data: {activeTabIndex: 1},
                          });
                        }}
                        onAliasRemove={async values => {
                          const payload = {};

                          if (values) {
                            payload.aliases = values;
                          }

                          await httpClient.delete(
                            `/api/wordpress/${projectName}/wordpress-sites/${name}/aliases`,
                            {data: payload}
                          );

                          const wordpress_sites = await getWordpressSites(projectName);
                          dispatch({
                            type: 'SET_DATA',
                            data: {wordpress_sites: wordpress_sites},
                          });

                          dispatch({
                            type: 'SET_ACTIVE_ENV_TAB',
                            data: {activeTabIndex: 1},
                          });
                        }}
                      />
                    </Tab.Pane>
                  );
                },
              },
              {
                menuItem: 'Backup',
                render: function backup() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Message info>
                        <MessageHeader>Backup policy</MessageHeader>
                        <p>
                          This website will be backed up automatically every day around midnight. We
                          backup the filesystem and the database.
                        </p>
                      </Message>
                    </Tab.Pane>
                  );
                },
              },
              {
                menuItem: 'Web Analytics',
                render: function webAnalytics() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Matomo
                        key={name}
                        projectType="wordpress"
                        projectName={projectName}
                        siteUrl={wordpressSite.hostnames?.[0] ?? 'Primary hostname not available'}
                        siteId={wordpressSite.matomoSiteId}
                        siteResourceName={name}
                      />
                    </Tab.Pane>
                  );
                },
              },
            ]}
          />
        ))
      )}
    </Container>
  );
};

Environments.propTypes = {
  projectName: PropTypes.string.isRequired,
  project: PropTypes.object,
  onWordpressSites: PropTypes.func,
};

export default Environments;
